/**
 * space_war_authority.ts — THE COMBAT AUTHORITY PILOT on the client (space campaign slice D, 2026-09-30).
 *
 * A campaign opened under `combat_authority_v1` is PINNED (`war_authority`): its named lives (capitals, frigates,
 * corvettes) carry CANONICAL hp on the server (`war_life`), and a pilot's hit on one is an INTENT (`warShot`) the server
 * judges — the fire origin, the impact point and the bolt's age, never a damage number. On this client a hit is a
 * PREDICTED cue: the hull flinches and burns at once, and once a second the picture is reconciled to the row. A death
 * arrives only when the roster flips (C's one death writer), exactly as before.
 *
 * Pure rules (tested by verify:war-authority § CLIENT) and the thin send path. No three.js here.
 */
import { atlas } from '../state.js';
import { WAR_AUTHORITY_VERSION, MASS_LOCK_M, lifeRosterKey, warLifeKey, type LifeKind, type V3, type WarMount } from '@shared/war_authority.js';

export interface LifeRowLike {
  lifeKey: string; life: number; hp: number; hpMax: number; seq: number; lastBy: string;
  endedAt: { microsSinceUnixEpoch: bigint }; endCause: string;
}
interface PinRowLike { campaignId: bigint; pinned: boolean; version: number }
type AuthorityDb = {
  warAuthority?: { campaignId: { find(id: bigint): PinRowLike | undefined | null } };
  warLife?: { lifeKey: { find(k: string): LifeRowLike | undefined | null } };
};
const db = (): AuthorityDb | null => (atlas.conn?.db as unknown as AuthorityDb | undefined) ?? null;

/** The war judges every named hit on the server (pinned at its opening, not returned to the claim rules by a lever). */
export function warPinned(campaignId: bigint | null | undefined): boolean {
  if (campaignId === null || campaignId === undefined) return false;
  try { return !!db()?.warAuthority?.campaignId.find(campaignId)?.pinned; } catch { return false; }
}
/** The canonical row of a named life, or null (no pin, a pre-publish shard, a slot never fielded). */
export function warLifeRow(campaignId: bigint, kind: LifeKind, side: number, slot: number): LifeRowLike | null {
  try { return db()?.warLife?.lifeKey.find(warLifeKey(campaignId, lifeRosterKey(kind, side, slot))) ?? null; } catch { return null; }
}
export const lifeEnded = (r: LifeRowLike): boolean => r.endedAt.microsSinceUnixEpoch > 0n;

// ── Where a shot left from (the server re-builds the bolt from it) ───────────────────────────────────────────────
const fired = new WeakMap<object, { x: number; y: number; z: number; atMs: number }>();
/** Stamp a projectile with its ABSOLUTE fire origin and the wall clock (a bolt sprite / missile is new per shot). */
export function noteFired(projectile: object, x: number, y: number, z: number): void {
  fired.set(projectile, { x, y, z, atMs: Date.now() });
}
export function firedFrom(projectile: object | undefined): { x: number; y: number; z: number; atMs: number } | null {
  return projectile ? fired.get(projectile) ?? null : null;
}

// ── The per-mount sequence (the server's 64-wide receive window) ────────────────────────────────────────────────
/** Monotone per mount and past every earlier session's: max(last + 1, wall-clock µs). */
export function nextSeqAfter(prev: bigint, nowMs: number): bigint {
  const floor = BigInt(Math.floor(nowMs)) * 1000n;
  return prev + 1n > floor ? prev + 1n : floor;
}
const seqs: Record<WarMount, bigint> = { gun: 0n, missile: 0n };

// ── What the pilot is told (only when it helps) ─────────────────────────────────────────────────────────────────
/** A refusal worth saying, or null. Races (the hull already down, its life ended, still arriving) and internal
 *  sequence repairs say nothing; a judged MISS is not a refusal at all. */
export function refusalToast(why: string): string | null {
  if (/outside the war zone/i.test(why)) return `Your hits do not count from here — ${why.replace(/^.*?(outside the war zone)/i, 'you are $1')}`;
  if (/faster than your cannons|Capacitor dry|rack is still cycling|rack is empty/i.test(why)) return `The record caps your fire: ${why}`;
  if (/still being fought on the record/i.test(why)) return why;
  if (/combat rules v\d+/i.test(why)) return `${why} (your client is out of date)`;
  if (/claim rules/i.test(why)) return why;
  if (/has not been|flies that far|outside the window/i.test(why)) return `The record could not place that hit: ${why}`;
  if (/docked|transit|in flight|not in sys|system/i.test(why)) return `No hits count: ${why}`;
  return null;
}
/** A sequence the server will never take again: start the mount's sequence past it. */
export const seqRepair = (why: string): boolean => /Stale shot sequence|already used for a different shot/i.test(why);

export const warShotStats = { sent: 0, accepted: 0, refused: 0, misses: 0, lastWhy: '', lastToastAt: 0, byWhy: {} as Record<string, number> };
/** The capital I last hit (the HUD names it with the record's hull). */
let mark: { side: number; k: number; atMs: number } | null = null;
export function lastMark(maxAgeMs: number): { side: number; k: number } | null {
  return mark && Date.now() - mark.atMs <= maxAgeMs ? mark : null;
}
/** When my last intent left (a pending escort waits for its answer before the row restores it). */
let lastIntentAt = 0;
export const msSinceIntent = (): number => Date.now() - lastIntentAt;

/** Send ONE intent for a hit this client SAW. Returns false when nothing was sent (no pin, no row, an ended life, no
 *  origin). The predicted cue is the caller's; the row reconciles it. */
export function sendWarShot(s: {
  campaignId: bigint; kind: LifeKind; side: number; slot: number; mount: WarMount;
  origin: { x: number; y: number; z: number; atMs: number }; impact: V3;
  /** The projectile's FLIGHT time in sim seconds (its life − ttl). The bolt flew in the fixed sim — on a slow client a
   *  1.6 s bolt can take several wall seconds — so the reach the record allows is judged on this, not the wall clock. */
  ageS?: number;
}, toast: (msg: string) => void): boolean {
  const conn = atlas.conn;
  if (!conn || !warPinned(s.campaignId)) return false;
  const row = warLifeRow(s.campaignId, s.kind, s.side, s.slot);
  if (!row || lifeEnded(row)) return false;
  const now = Date.now();
  const ageMs = Math.max(0, Math.min(60_000, Math.round(s.ageS !== undefined && Number.isFinite(s.ageS) ? s.ageS * 1000 : now - s.origin.atMs)));
  const seq = seqs[s.mount] = nextSeqAfter(seqs[s.mount], now);
  if (s.kind === 'capital') mark = { side: s.side, k: s.slot, atMs: now };
  lastIntentAt = now;
  warShotStats.sent++;
  const reducers = conn.reducers as unknown as { warShot(a: Record<string, unknown>): Promise<void> };
  reducers.warShot({
    campaignId: s.campaignId, target: lifeRosterKey(s.kind, s.side, s.slot), life: row.life, mount: s.mount, seq, ageMs,
    ox: s.origin.x, oy: s.origin.y, oz: s.origin.z, ix: s.impact.x, iy: s.impact.y, iz: s.impact.z, version: WAR_AUTHORITY_VERSION,
  }).then(() => { warShotStats.accepted++; }).catch((e: Error) => {
    const why = String(e?.message ?? e);
    warShotStats.refused++;
    warShotStats.lastWhy = why;
    const tag = why.replace(/\(.*$/, '').trim().slice(0, 48);
    warShotStats.byWhy[tag] = (warShotStats.byWhy[tag] ?? 0) + 1;
    if (seqRepair(why)) seqs[s.mount] = nextSeqAfter(seqs[s.mount] + 100_000n, Date.now());
    const say = refusalToast(why);
    if (say && Date.now() - warShotStats.lastToastAt > 5000) { warShotStats.lastToastAt = Date.now(); toast(say); }
  });
  return true;
}

// ── The capital step's scratch (the renderer runs `capKinStep` 60 times a second per hull — no allocation) ──────
const kinScratch: V3[] = [];
/** A cleared, reused list for `capKinStep`'s `others` (the caller fills it before each call). */
export function kinOthers(): V3[] { kinScratch.length = 0; return kinScratch; }

// ── THE MASS LOCK (movement honesty: the drive will not jump inside a pinned war's theatre) ──────────────────────
export function massLocked(distToCentreM: number, pinnedLive: boolean): boolean {
  return pinnedLive && distToCentreM <= MASS_LOCK_M;
}

// ── THE LETHAL SALVO (a round's named death is drawn from the killer line at THAT hull) ──────────────────────────
/** The killer line's nearest living capitals to the dying hull, nearest first (at most `n`). */
export function salvoShooters(target: V3, line: ReadonlyArray<{ k: number; p: V3; alive: boolean }>, n = 3): number[] {
  return line.filter((c) => c.alive)
    .map((c) => ({ k: c.k, d: Math.hypot(c.p.x - target.x, c.p.y - target.y, c.p.z - target.z) }))
    .sort((a, b) => a.d - b.d || a.k - b.k)
    .slice(0, Math.max(0, n)).map((c) => c.k);
}

/** The HUD's word on the record (text + icon, never colour alone). */
export function authorityHud(pinned: boolean, markLine: { name: string; hp: number; hpMax: number } | null): string {
  if (!pinned) return '';
  const m = markLine ? ` · your mark: ${markLine.name} — hull ${Math.max(0, Math.round(markLine.hp))}/${Math.round(markLine.hpMax)} (the record)` : '';
  return ` · ⚖ every hit judged on the record${m}`;
}

/** Probe telemetry (`__space.warAuthority()`). */
export function authorityTelemetry(): object {
  return { ...warShotStats, byWhy: { ...warShotStats.byWhy }, mark, seqs: { gun: String(seqs.gun), missile: String(seqs.missile) } };
}
